import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Copy, Check, Trash2, KeyRound, Plus, X, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";

import {
  listApiKeys,
  createApiKey,
  deleteApiKey,
  type ApiKey,
} from "@/lib/api-keys";
import { useMySubscription } from "@/hooks/queries/useMySubscription";
import { MCP_PLAN_NAMES } from "@/lib/domain-constants";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const CARD =
  "rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900";

export default function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const { data: subscription } = useMySubscription();

  // La key se genera igual: el backend es quien corta al usarla. Acá solo se
  // avisa, para que nadie la conecte y descubra el bloqueo por prueba y error.
  const planAllowsApi =
    !subscription || MCP_PLAN_NAMES.includes(subscription.planKey);

  async function load() {
    setLoading(true);
    try {
      setKeys(await listApiKeys());
    } catch {
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function create() {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const data = await createApiKey(name.trim());
      setCreatedKey(data?.key ?? null);
      setName("");
      load();
    } catch {
      toast.error("No se pudo generar la API key");
    } finally {
      setCreating(false);
    }
  }

  async function remove(id: string) {
    setRevokingId(id);
    try {
      await deleteApiKey(id);
      toast.success("API key revocada");
      load();
    } catch {
      toast.error("No se pudo revocar la API key");
    } finally {
      setRevokingId(null);
    }
  }

  function copy(value: string) {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {!planAllowsApi && (
        <div className="flex flex-col gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
          <div className="flex items-center gap-2 text-xs font-bold text-amber-700 dark:text-amber-400">
            <Lock className="size-4" />
            El acceso por API es de los planes Comercial, Contratista y Corporativo
          </div>
          <p className="text-sm text-amber-700/80 dark:text-amber-400/80">
            Puedes generar tu key ahora, pero las llamadas se rechazarán hasta
            que subas de plan.{" "}
            <Link to="/settings/plan" className="font-bold underline">
              Ver planes
            </Link>
          </p>
        </div>
      )}

      {/* Create new key */}
      <div className={cn(CARD, "p-6")}>
        <div className="mb-5">
          <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
            Nueva API Key
          </h2>
          <p className="mt-0.5 text-sm text-slate-400 dark:text-slate-500">
            Úsala para autenticarte en la API de Licitigo desde sistemas
            externos
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
            placeholder="Nombre de la key (ej. Producción, MCP Server...)"
            className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
          <button
            onClick={create}
            disabled={creating || !name.trim()}
            className="flex shrink-0 items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="size-4" />
            {creating ? "Generando..." : "Generar"}
          </button>
        </div>

        {/* One-time display of the newly generated key */}
        {createdKey && (
          <div className="mt-4 flex flex-col gap-2 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-900/50 dark:bg-green-950/20">
            <div className="flex items-center gap-2 text-xs font-bold text-green-700 dark:text-green-400">
              <CheckCircle2 className="size-4" />
              API Key generada — cópiala ahora, no se volverá a mostrar
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-lg bg-green-100/60 px-3 py-2 font-mono text-xs text-green-800 dark:bg-green-900/30 dark:text-green-300">
                {createdKey}
              </code>
              <button
                onClick={() => copy(createdKey)}
                className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {copied ? (
                  <Check className="size-3.5" />
                ) : (
                  <Copy className="size-3.5" />
                )}
                {copied ? "Copiada" : "Copiar"}
              </button>
              <button
                onClick={() => setCreatedKey(null)}
                className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Keys list */}
      <div className={cn(CARD, "overflow-hidden")}>
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800">
          <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
            Keys activas
          </h2>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-400 dark:bg-slate-800">
            {keys.length} {keys.length === 1 ? "key" : "keys"}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="size-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : keys.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <div className="flex size-12 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800">
              <KeyRound className="size-6 text-slate-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">
                Sin API keys
              </p>
              <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
                Genera una para empezar
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {keys.map((k) => (
              <div
                key={k.id}
                className="flex items-center justify-between gap-4 px-6 py-4 transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/20"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-sm font-bold text-slate-800 dark:text-slate-100">
                    {k.name || "Sin nombre"}
                  </span>
                  <div className="flex flex-wrap items-center gap-3">
                    <code className="font-mono text-xs text-slate-400 dark:text-slate-500">
                      {k.start ? `${k.start}••••••••` : "••••••••"}
                    </code>
                    <span className="text-[10px] text-slate-300 dark:text-slate-600">
                      ·
                    </span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                      Creada {formatDate(k.createdAt)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => remove(k.id)}
                  disabled={revokingId === k.id}
                  className="flex shrink-0 items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/50 dark:hover:bg-red-950/20"
                >
                  <Trash2 className="size-3.5" />
                  {revokingId === k.id ? "Revocando..." : "Revocar"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
